"use client";

import React, { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Plus,
  Search,
  Calendar,
  Tag,
  FileText,
  RotateCcw,
  Copy,
} from "lucide-react";
import { usePagination } from "@/hooks/use-pagination";
import { SimpleLoader } from "@/components/ui/simple-loader";

interface BlogTableProps {
  onCreateBlog: () => void;
  onEditBlog: (blogId: Id<"blog">) => void;
}

export function BlogTable({ onCreateBlog, onEditBlog }: BlogTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "published" | "draft"
  >("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState<Id<"blog"> | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [blogToDuplicate, setBlogToDuplicate] = useState<Id<"blog"> | null>(
    null
  );
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [duplicateSlug, setDuplicateSlug] = useState("");

  // pagination
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // queries
  const blogs = useQuery(api.blog.getAllBlogPosts, {
    limit: itemsPerPage,
    offset: (currentPage - 1) * itemsPerPage,
    published:
      statusFilter === "all" ? undefined : statusFilter === "published",
    searchTerm: searchTerm || undefined,
  });

  const pagination = usePagination({
    totalItems: blogs?.total || 0,
    itemsPerPage,
  });

  // mutations
  const deleteBlog = useMutation(api.blog.deleteBlogPost);
  const restoreBlog = useMutation(api.blog.restoreBlogPost);
  const updateBlog = useMutation(api.blog.updateBlogPost);
  const duplicateBlog = useMutation(api.blog.duplicateBlogPost);

  // handle delete
  const handleDelete = async () => {
    if (!blogToDelete) return;

    try {
      await deleteBlog({
        id: blogToDelete,
        deleted_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
      });
      toast.success("Blog post deleted successfully");
      setShowDeleteDialog(false);
      setBlogToDelete(null);
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog post");
    }
  };

  // handle restore
  const handleRestore = async (blogId: Id<"blog">) => {
    try {
      await restoreBlog({
        id: blogId,
        updated_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
      });
      toast.success("Blog post restored successfully");
    } catch (error) {
      console.error("Error restoring blog:", error);
      toast.error("Failed to restore blog post");
    }
  };

  // handle toggle published status
  const handleTogglePublished = async (
    blogId: Id<"blog">,
    currentStatus: boolean
  ) => {
    try {
      await updateBlog({
        id: blogId,
        published: !currentStatus,
        updated_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
      });
      toast.success(
        `Blog post ${!currentStatus ? "published" : "unpublished"} successfully`
      );
    } catch (error) {
      console.error("Error toggling published status:", error);
      toast.error("Failed to update blog post");
    }
  };

  // handle duplicate
  const handleDuplicate = async () => {
    if (!blogToDuplicate || !duplicateTitle.trim() || !duplicateSlug.trim()) {
      return;
    }

    try {
      await duplicateBlog({
        id: blogToDuplicate,
        newSlug: duplicateSlug.trim(),
        newTitle: duplicateTitle.trim(),
        created_by: "blogmaster@blirumahproperty.com", // this will be replaced with actual user
      });
      toast.success("Blog post duplicated successfully");
      setShowDuplicateDialog(false);
      setBlogToDuplicate(null);
      setDuplicateTitle("");
      setDuplicateSlug("");
    } catch (error) {
      console.error("Error duplicating blog:", error);
      toast.error("Failed to duplicate blog post");
    }
  };

  // handle duplicate dialog open
  const handleDuplicateDialogOpen = (
    blogId: Id<"blog">,
    originalTitle: string,
    originalSlug: string
  ) => {
    setBlogToDuplicate(blogId);
    setDuplicateTitle(`${originalTitle} (Copy)`);
    setDuplicateSlug(`${originalSlug}-copy`);
    setShowDuplicateDialog(true);
  };

  // format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!blogs) {
    return (
      <div className="flex items-center justify-center p-8">
        <SimpleLoader message="Loading blogs..." />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Blog Posts</h2>
          <p className="text-xs text-muted-foreground">
            Manage your blog posts and content
          </p>
        </div>
        <Button onClick={onCreateBlog} size="sm" className="h-8 active:scale-95 transition-all duration-150 ease-out cursor-pointer">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Create New Blog
        </Button>
      </div>

      {/* filters */}
      <Card>
        <CardHeader className="px-3 pt-3 pb-2">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="search" className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-muted-foreground h-3.5 w-3.5" />
                <Input
                  id="search"
                  placeholder="Search by title, content, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
              </div>
            </div>
            <div className="sm:w-40">
              <Label htmlFor="status" className="text-xs">Status</Label>
              <Select
                value={statusFilter}
                onValueChange={(value: any) => setStatusFilter(value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Posts</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Drafts</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* table */}
      <Card>
        <CardContent className="p-0">
          <div className="rounded-md border overflow-y-auto max-h-[calc(100vh-300px)]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blogs.posts.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-6 text-xs text-muted-foreground"
                    >
                      No blog posts found
                    </TableCell>
                  </TableRow>
                ) : (
                  blogs.posts.map((blog) => (
                    <TableRow key={blog._id}>
                      <TableCell>
                        <div className="space-y-0.5">
                          <div className="font-medium text-xs truncate">{blog.title}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            /{blog.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={blog.published ? "default" : "secondary"} className="text-xs">
                          {blog.published ? "Published" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-0.5">
                          {blog.tags.slice(0, 3).map((tag, index) => (
                            <Badge
                              key={index}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {blog.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{blog.tags.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 shrink-0" />
                          {formatDate(blog.created_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleTogglePublished(
                                blog._id,
                                blog.published || false
                              )
                            }
                            className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            {blog.published ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditBlog(blog._id)}
                            className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleDuplicateDialogOpen(
                                blog._id,
                                blog.title,
                                blog.slug
                              )
                            }
                            className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          {blog.deleted_status ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestore(blog._id)}
                              className="h-7 w-7 p-0 active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setBlogToDelete(blog._id);
                                setShowDeleteDialog(true);
                              }}
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive active:scale-95 transition-all duration-150 ease-out cursor-pointer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* pagination */}
      {blogs.total > itemsPerPage && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Showing{" "}
            {Math.min((currentPage - 1) * itemsPerPage + 1, blogs.total)} to{" "}
            {Math.min(currentPage * itemsPerPage, blogs.total)} of {blogs.total}{" "}
            posts
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="h-7 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            >
              Previous
            </Button>
            <span className="text-xs">
              Page {currentPage} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages}
              className="h-7 text-xs active:scale-95 transition-all duration-150 ease-out cursor-pointer"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Blog Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this blog post? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* duplicate blog dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Blog Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="duplicate-title">Title</Label>
              <Input
                id="duplicate-title"
                value={duplicateTitle}
                onChange={(e) => setDuplicateTitle(e.target.value)}
                placeholder="Enter title for duplicated post"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="duplicate-slug">Slug</Label>
              <Input
                id="duplicate-slug"
                value={duplicateSlug}
                onChange={(e) => setDuplicateSlug(e.target.value)}
                placeholder="Enter slug for duplicated post"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              The duplicated post will be created as a draft with the same
              content, tags, and metadata as the original.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDuplicateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDuplicate}
                disabled={!duplicateTitle.trim() || !duplicateSlug.trim()}
              >
                Duplicate
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
